<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from './PosUiIcon.vue';
  import { normalizeStepperValue } from './pos-ui-stepper.utils';

  type TSize = 'sm' | 'md' | 'lg';

  const props = withDefaults(
    defineProps<{
      value: number;
      min?: number;
      max?: number;
      step?: number;
      size?: TSize;
      disabled?: boolean;
      /** Lets the value be typed; off by default so POS taps never open a keyboard. */
      editable?: boolean;
      ariaLabel?: string;
    }>(),
    {
      min: 0,
      max: Infinity,
      step: 1,
      size: 'md',
      disabled: false,
      editable: false,
      ariaLabel: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'input', value: number): void;
    /** Typed text asked for more than `max`; the value was clamped to it. */
    (e: 'clamped', requested: number): void;
  }>();

  const canDecrement = computed(
    () => !props.disabled && props.value > props.min
  );
  const canIncrement = computed(
    () => !props.disabled && props.value < props.max
  );

  function decrement() {
    if (!canDecrement.value) return;
    emit('input', Math.max(props.min, props.value - props.step));
  }

  function increment() {
    if (!canIncrement.value) return;
    emit('input', Math.min(props.max, props.value + props.step));
  }

  function bounds() {
    return { min: props.min, max: props.max, step: props.step };
  }

  /** Shows the accepted value even when the prop does not change, emits only on change. */
  function accept(input: HTMLInputElement, next: number) {
    input.value = String(next);
    if (next !== props.value) emit('input', next);
  }

  /** One commit per edit: native `change` fires on blur and on Enter (which blurs). */
  function commit(event: Event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    const typed = event.target.value;
    accept(event.target, normalizeStepperValue(typed, bounds()) ?? props.value);
    const requested = normalizeStepperValue(typed, {
      ...bounds(),
      max: Infinity,
    });
    if (requested !== null && requested > props.max) {
      emit('clamped', requested);
    }
  }

  /** Arrow keys step from the text being typed, not from the last committed value. */
  function stepFromInput(event: KeyboardEvent, direction: 1 | -1) {
    if (!(event.target instanceof HTMLInputElement) || props.disabled) return;
    const base =
      normalizeStepperValue(event.target.value, bounds()) ?? props.value;
    accept(
      event.target,
      Math.min(props.max, Math.max(props.min, base + direction * props.step))
    );
  }

  function blurInput(event: KeyboardEvent) {
    if (event.target instanceof HTMLInputElement) event.target.blur();
  }

  /**
   * Typing replaces the value: select all on focus and keep that selection
   * through the mouseup of the click that focused the input (browsers would
   * otherwise collapse it to a caret, and iOS only honours select() from a
   * tap). Later clicks place the caret normally.
   */
  let keepSelectionOnMouseUp = false;

  function selectAllOnFocus(event: FocusEvent) {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.target.select();
    keepSelectionOnMouseUp = true;
  }

  function keepSelectionOnce(event: MouseEvent) {
    if (!keepSelectionOnMouseUp) return;
    keepSelectionOnMouseUp = false;
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    event.target.select();
  }
</script>

<template>
  <div
    class="pos-ui-stepper"
    :class="[`size-${size}`, { 'is-disabled': disabled }]"
    :aria-label="editable ? undefined : ariaLabel"
  >
    <button
      class="pos-ui-stepper-btn"
      type="button"
      :disabled="!canDecrement"
      :aria-label="$t('posV2.decrease')"
      @click="decrement"
    >
      <PosUiIcon name="minus" />
    </button>
    <input
      v-if="editable"
      class="pos-ui-stepper-input"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      :value="value"
      :disabled="disabled"
      :aria-label="ariaLabel"
      @change="commit"
      @focus="selectAllOnFocus"
      @mouseup="keepSelectionOnce"
      @keydown.enter.prevent="blurInput"
      @keydown.up.prevent="stepFromInput($event, 1)"
      @keydown.down.prevent="stepFromInput($event, -1)"
    />
    <span v-else class="pos-ui-stepper-value">{{ value }}</span>
    <button
      class="pos-ui-stepper-btn"
      type="button"
      :disabled="!canIncrement"
      :aria-label="$t('posV2.increase')"
      @click="increment"
    >
      <PosUiIcon name="plus" />
    </button>
  </div>
</template>

<style scoped lang="scss">
  .pos-ui-stepper {
    @apply tw-inline-flex tw-items-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-300 tw-bg-surface;

    &.is-disabled {
      @apply tw-opacity-60;
    }

    &.size-sm {
      .pos-ui-stepper-btn {
        @apply tw-h-8 tw-w-8 tw-text-base;
      }
      .pos-ui-stepper-value,
      .pos-ui-stepper-input {
        @apply tw-min-w-[2rem] tw-text-sm;
      }
      .pos-ui-stepper-input {
        @apply tw-h-8 tw-w-12;
      }
    }
    &.size-md {
      .pos-ui-stepper-btn {
        @apply tw-h-11 tw-w-11 tw-text-xl;
      }
      .pos-ui-stepper-value,
      .pos-ui-stepper-input {
        @apply tw-min-w-[2.5rem] tw-text-base;
      }
      .pos-ui-stepper-input {
        @apply tw-h-11 tw-w-14;
      }
    }
    &.size-lg {
      .pos-ui-stepper-btn {
        @apply tw-h-12 tw-w-14 tw-text-2xl;
      }
      .pos-ui-stepper-value,
      .pos-ui-stepper-input {
        @apply tw-min-w-[3rem] tw-text-lg;
      }
      .pos-ui-stepper-input {
        @apply tw-h-12 tw-w-20;
      }
    }

    .pos-ui-stepper-btn {
      @apply tw-flex tw-items-center tw-justify-center tw-text-neutral-600 tw-transition-colors hover:tw-bg-neutral-100 active:tw-bg-neutral-200;

      &:focus-visible {
        // Inset: the rounded root clips an outside ring.
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-300 hover:tw-bg-transparent;
      }
    }

    .pos-ui-stepper-value {
      @apply tw-select-none tw-text-center tw-font-semibold tw-tabular-nums tw-text-neutral-900;
    }

    // Hairline dividers make the number read as a field, not a label.
    .pos-ui-stepper-input {
      @apply tw-border-y-0 tw-border-x tw-border-solid tw-border-neutral-200 tw-bg-transparent tw-p-0 tw-text-center tw-font-semibold tw-tabular-nums tw-text-neutral-900 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-orange-500/40;
      appearance: textfield;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        appearance: none;
      }
      &:disabled {
        @apply tw-cursor-not-allowed;
      }
    }
  }
</style>
